import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaTrophy, FaLightbulb, FaClock, FaFire } from 'react-icons/fa';
import MentorProfile from './MentorProfile';
import Testimonials from './Testimonials';
import WebinarSchedule from './WebinarSchedule';
import Pricing from './Pricing';
import CTA from './CTA';
import FloatingCTA from './FloatingCTA';
import { saveUTMParams, trackViewContent } from '../utils/tracking';

function LP1() {
  useEffect(() => {
    saveUTMParams();
    trackViewContent({
      content_name: 'News Hack LP1 - Opportunity 2025',
      content_category: 'Trading Course',
      content_type: 'landing_page_variant_1'
    });
  }, []);

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section - Opportunity Angle */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-darkBlue via-darkerBlue to-darkBlue text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 text-primary text-[200px] font-bold animate-pulse">$</div>
          <div className="absolute bottom-10 left-10 text-primary text-[200px] font-bold animate-pulse">¥</div>
        </div>

        <div className="container max-w-6xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-600/20 to-yellow-500/20 border border-yellow-500/30 rounded-full px-6 py-3 mb-8">
              <FaFire className="text-yellow-500 animate-pulse" />
              <span className="text-yellow-500 font-bold uppercase tracking-wider">Peluang Emas 2025</span>
              <FaFire className="text-yellow-500 animate-pulse" />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Bank Sentral Dunia Bersiap<br/>
              <span className="text-primary text-5xl md:text-6xl lg:text-7xl">Ubah Kebijakan Besar</span>
            </h1>

            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto text-textGray">
              Fed, ECB, dan BOJ diprediksi akan mengambil keputusan historis di 2025.
              <span className="text-primary font-bold block mt-2">
                Trader yang siap bisa meraup 500-1000 pips per bulan!
              </span>
            </p>

            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gradient-to-r from-red-900/30 to-red-800/30 border border-red-500/30 rounded-2xl p-8 max-w-3xl mx-auto mb-8"
            >
              <h3 className="text-2xl font-bold mb-4 text-red-400">
                ⚠️ PERINGATAN: Tanpa Strategi yang Tepat...
              </h3>
              <p className="text-lg text-textGray">
                95% trader akan kehilangan kesempatan emas ini atau bahkan rugi besar karena volatilitas ekstrem.
                <span className="text-primary font-bold block mt-2">
                  Jangan jadi bagian dari statistik ini!
                </span>
              </p>
            </motion.div>

            <motion.a
              href="/checkout"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-gradient-to-r from-primaryDark to-primary hover:from-primary hover:to-primaryLight text-darkBlue font-bold py-5 px-12 rounded-full text-xl md:text-2xl shadow-2xl"
            >
              Pelajari Strategi NEWS HACK Sekarang →
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Why 2025 is Different */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-darkerBlue to-darkBlue text-white">
        <div className="container max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Kenapa 2025 Berbeda dari Tahun-Tahun Sebelumnya?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-darkGray/50 to-darkGray/30 rounded-xl p-6 border border-primary/20"
            >
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Siklus Ekonomi Berubah</h3>
              <p className="text-textGray text-center">
                Setelah era suku bunga tinggi, bank sentral mulai pivoting. 
                <span className="text-primary font-semibold"> Momentum terbaik untuk news trader!</span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-darkGray/50 to-darkGray/30 rounded-xl p-6 border border-primary/20"
            >
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrophy className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Volatilitas = Profit</h3>
              <p className="text-textGray text-center">
                Perubahan kebijakan = volatilitas tinggi = peluang profit besar.
                <span className="text-primary font-semibold"> NFP & CPI jadi "jackpot" trader!</span>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-darkGray/50 to-darkGray/30 rounded-xl p-6 border border-primary/20"
            >
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaLightbulb className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Algoritma Belum Siap</h3>
              <p className="text-textGray text-center">
                Trading algo masih adjust dengan pola baru.
                <span className="text-primary font-semibold"> Human trader dengan strategi tepat unggul!</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-16 bg-gradient-to-b from-darkBlue to-darkerBlue text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl p-8 md:p-12 border border-primary/30"
          >
            <FaClock className="text-5xl text-primary mx-auto mb-4 animate-pulse" />
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Window Opportunity Terbatas!
            </h3>
            <p className="text-lg md:text-xl text-textGray mb-6">
              Momen seperti ini hanya datang 1-2 kali dalam satu dekade.
              <span className="text-primary font-bold block mt-2">
                Trader yang bersiap SEKARANG akan menuai hasilnya sepanjang 2025-2026.
              </span>
            </p>
            <motion.a
              href="/checkout"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-gradient-to-r from-primaryDark to-primary hover:from-primary hover:to-primaryLight text-darkBlue font-bold py-4 px-10 rounded-full text-lg md:text-xl shadow-2xl"
            >
              Jangan Lewatkan Kesempatan Ini →
            </motion.a>
          </motion.div>
        </div>
      </section>

      <MentorProfile />
      <Testimonials />
      <WebinarSchedule />
      <Pricing />
      <CTA />
      <FloatingCTA />
    </div>
  );
}

export default LP1;